import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export const buildSearchOptions = (identifierValue, identifierLabel) => [
  { value: 'client', name: 'Client' },
  { value: 'insurance', name: 'Insurance' },
  { value: identifierValue, name: identifierLabel },
  { value: 'ro', name: 'RO #' },
  { value: 'vehicle', name: 'Vehicle' },
  { value: 'vin', name: 'VIN' }
]

// Build the derived searchFilters array from primary/secondary/multi values.
// Pulled out of the hook so we can seed the initial state synchronously, which
// avoids a "fetch with empty filters → fetch with real filters" flicker on
// pages that hydrate from URL.
const buildSearchFilters = ({ primarySearchBy, primaryQuery, secondarySearchBy, secondaryQuery, multiFilters }) => {
  const trimmedPrimary = (primaryQuery || '').trim()
  const trimmedSecondary = (secondaryQuery || '').trim()
  const multiEntries = Object.entries(multiFilters || {})
    .map(([col, val]) => [col, (val || '').trim()])
    .filter(([, val]) => val.length > 0)
    .map(([col, val]) => ({ column: col, value: [val] }))
  return [
    ...(trimmedPrimary ? [{ column: primarySearchBy, value: [trimmedPrimary] }] : []),
    ...(trimmedSecondary ? [{ column: secondarySearchBy, value: [trimmedSecondary] }] : []),
    ...multiEntries
  ]
}

export const useSearchFilters = ({ identifierValue, identifierLabel, debounceMs = 350, onFiltersChange, initialValues = {} }) => {
  // initialValues lets the caller hydrate from URL search params (or any other
  // source) so the filter chips repopulate after a back-navigation. Each field
  // falls back to its long-standing default if the caller doesn't supply one.
  const initialPrimarySearchBy = initialValues.primarySearchBy || 'ro'
  const initialPrimaryQuery = initialValues.primaryQuery || ''
  const initialSecondarySearchBy = initialValues.secondarySearchBy || 'vin'
  const initialSecondaryQuery = initialValues.secondaryQuery || ''
  const initialMulti = initialValues.multiFilters || {}
  const [primarySearchBy, setPrimarySearchBy] = useState(initialPrimarySearchBy)
  const [primaryQuery, setPrimaryQuery] = useState(initialPrimaryQuery)
  const [secondarySearchBy, setSecondarySearchBy] = useState(initialSecondarySearchBy)
  const [secondaryQuery, setSecondaryQuery] = useState(initialSecondaryQuery)
  const [multiFilters, setMultiFilters] = useState(initialMulti)
  // Seed the derived list synchronously so the first render already exposes
  // the correct searchFilters; otherwise the consumer's fetch effect would
  // briefly run with [] before the debounced effect below caught up.
  const initialSearchFilters = useRef(
    buildSearchFilters({
      primarySearchBy: initialPrimarySearchBy,
      primaryQuery: initialPrimaryQuery,
      secondarySearchBy: initialSecondarySearchBy,
      secondaryQuery: initialSecondaryQuery,
      multiFilters: initialMulti
    })
  )
  const [searchFilters, setSearchFilters] = useState(initialSearchFilters.current)
  const onFiltersChangeRef = useRef(onFiltersChange)
  const lastFiltersRef = useRef(initialSearchFilters.current)

  const searchOptions = useMemo(() => buildSearchOptions(identifierValue, identifierLabel), [identifierLabel, identifierValue])

  // Clear every search-by chip back to its default in one shot (used when a
  // page's status / segment tab changes). The derived list is reset
  // synchronously — and lastFiltersRef kept in step — so the consumer's fetch
  // effect runs once with empty filters instead of waiting out the debounce and
  // firing a redundant follow-up.
  const resetSearchFilters = useCallback(() => {
    setPrimarySearchBy('ro')
    setPrimaryQuery('')
    setSecondarySearchBy('vin')
    setSecondaryQuery('')
    setMultiFilters({})
    lastFiltersRef.current = []
    setSearchFilters([])
  }, [])

  useEffect(() => {
    onFiltersChangeRef.current = onFiltersChange
  }, [onFiltersChange])

  useEffect(() => {
    const performSearch = () => {
      const updatedSearchFilters = buildSearchFilters({
        primarySearchBy,
        primaryQuery,
        secondarySearchBy,
        secondaryQuery,
        multiFilters
      })

      if (JSON.stringify(lastFiltersRef.current) === JSON.stringify(updatedSearchFilters)) {
        return
      }
      lastFiltersRef.current = updatedSearchFilters
      setSearchFilters(updatedSearchFilters)
      onFiltersChangeRef.current?.(updatedSearchFilters)
    }

    const timeoutId = setTimeout(performSearch, debounceMs)
    return () => clearTimeout(timeoutId)
  }, [debounceMs, primaryQuery, primarySearchBy, secondaryQuery, secondarySearchBy, multiFilters])

  return {
    primarySearchBy,
    setPrimarySearchBy,
    primaryQuery,
    setPrimaryQuery,
    secondarySearchBy,
    setSecondarySearchBy,
    secondaryQuery,
    setSecondaryQuery,
    multiFilters,
    setMultiFilters,
    searchFilters,
    searchOptions,
    resetSearchFilters
  }
}
